import React, { useState } from "react";
import "./Form1040Flow.css";

const Form1040Flow = () => {
  const [filingStatus, setFilingStatus] = useState("single"); // "single" or "mfj"
  const [income, setIncome] = useState("");

  // Credits inputs
  const [numQualifyingChildren, setNumQualifyingChildren] = useState("");
  const [numOtherDependents, setNumOtherDependents] = useState("");

  const [saverUse, setSaverUse] = useState(false);

  // Saver's credit + retirement contributions
  const [saverSingleContribution, setSaverSingleContribution] = useState("");
  const [saverSingleType, setSaverSingleType] = useState("traditional"); // "traditional" | "roth"

  const [saverSpouse1Contribution, setSaverSpouse1Contribution] =
    useState("");
  const [saverSpouse1Type, setSaverSpouse1Type] = useState("traditional");

  const [saverSpouse2Contribution, setSaverSpouse2Contribution] =
    useState("");
  const [saverSpouse2Type, setSaverSpouse2Type] = useState("traditional");

  // --- Core numeric spine ---

  const numericIncome = parseNumber(income);

  // derive deductible retirement adjustment FROM the saver inputs (traditional only)
  const saverInputs = getSaverInputs({
    filingStatus,
    saverUse,
    saverSingleContribution,
    saverSingleType,
    saverSpouse1Contribution,
    saverSpouse1Type,
    saverSpouse2Contribution,
    saverSpouse2Type,
  });

  const numericAdjustment = saverInputs.totalDeductible; // only traditional IRAs reduce AGI
  const agi = Math.max(0, numericIncome - numericAdjustment);

  const stdDeduction = getStandardDeduction(filingStatus);
  const taxableIncome = Math.max(0, agi - stdDeduction);
  const taxBeforeCredits = calculateTax(taxableIncome, filingStatus);

  // --- Nonrefundable credits: CTC, ODC, Saver's ---

  const nQC = Math.max(0, parseIntSafe(numQualifyingChildren));
  const nOD = Math.max(0, parseIntSafe(numOtherDependents));

  const CTC_PER_CHILD = 2000;
  const ODC_PER_DEP = 500;

  const ctcCalc = nQC * CTC_PER_CHILD;
  const odcCalc = nOD * ODC_PER_DEP;

  const saverRate = getSaversCreditRate(agi, filingStatus);
  const saverCalc = Math.round(saverInputs.totalEligible * saverRate);

  const nonrefResults = applyNonrefCreditsInOrder(taxBeforeCredits, [
    { key: "ctc", label: "Child Tax Credit (nonrefundable)", amount: ctcCalc },
    { key: "odc", label: "Other Dependent Credit", amount: odcCalc },
    { key: "saver", label: "Saver's Credit", amount: saverCalc },
  ]);

  const taxAfterNonref = nonrefResults.remainingTax;
  const totalNonrefUsed = nonrefResults.totalUsed;
  const totalNonrefUnused = nonrefResults.totalUnused;

  // For now, refundable credits = 0 (we'll add them later)
  const refundableTotal = 0;
  const finalTaxAfterAllCredits = taxAfterNonref - refundableTotal;

  return (
    <div className="f1040-container">
      <div className="f1040-layout">
        {/* LEFT: 1040 spine top → bottom */}
        <div className="f1040-left">
          <section className="f1040-card f1040-card-spine">
            <h2>Form 1040 spine (top to bottom)</h2>
            <div className="f1040-spine">
              <SpineStep
                step="1"
                label="Total income"
                value={formatCurrency(numericIncome)}
                explanation="Single combined income line for this example."
              />
              <SpineStep
                step="2"
                label="Adjustments (IRA deduction)"
                value={formatCurrency(numericAdjustment)}
                explanation="Traditional IRA contributions only, from the Saver's section."
              />
              <SpineStep
                step="3"
                label="Adjusted Gross Income (AGI)"
                value={formatCurrency(agi)}
                explanation="AGI = Income – deductible retirement contributions."
              />
              <SpineStep
                step="4"
                label="Standard deduction"
                value={formatCurrency(stdDeduction)}
                explanation={`Based on filing status: ${filingStatusLabel(
                  filingStatus
                )}.`}
              />
              <SpineStep
                step="5"
                label="Taxable income"
                value={formatCurrency(taxableIncome)}
                explanation="Taxable income = max(0, AGI – standard deduction)."
              />
              <SpineStep
                step="6"
                label="Tax before credits"
                value={formatCurrency(taxBeforeCredits)}
                explanation="Calculated from rough 2025-style brackets."
              />
              <SpineStep
                step="7"
                label="Nonrefundable credits used"
                value={formatCurrency(totalNonrefUsed)}
                explanation={`CTC, ODC, and Saver's Credit applied up to your tax. Unused: ${formatCurrency(
                  totalNonrefUnused
                )}.`}
              />
              <SpineStep
                step="8"
                label="Tax after nonrefundable credits"
                value={formatCurrency(taxAfterNonref)}
              />
              <SpineStep
                step="9"
                label="Refundable credits"
                value={formatCurrency(refundableTotal)}
                explanation="Currently set to 0 in this version."
              />
              <SpineStep
                step="10"
                label="Final tax after all credits"
                value={formatCurrency(finalTaxAfterAllCredits)}
                explanation="Negative would mean net refundable once withholding/payments are included."
              />
            </div>
          </section>
        </div>

        {/* RIGHT: Inputs & credit details */}
        <div className="f1040-right">
          {/* Basics: filing status + income in one tight row */}
          <section className="f1040-card f1040-card-basics">
            <h2>Basics</h2>
            <div className="f1040-basics-row">
              <div className="f1040-field-group f1040-field-group-tight">
                <label className="f1040-label">Filing status</label>
                <div className="f1040-radio-group">
                  <label className="f1040-radio">
                    <input
                      type="radio"
                      name="filingStatus"
                      value="single"
                      checked={filingStatus === "single"}
                      onChange={(e) => setFilingStatus(e.target.value)}
                    />
                    <span>Single</span>
                  </label>
                  <label className="f1040-radio">
                    <input
                      type="radio"
                      name="filingStatus"
                      value="mfj"
                      checked={filingStatus === "mfj"}
                      onChange={(e) => setFilingStatus(e.target.value)}
                    />
                    <span>Married filing jointly</span>
                  </label>
                </div>
              </div>

              <div className="f1040-field-group f1040-field-group-tight">
                <label className="f1040-label" htmlFor="income-input">
                  Total income
                </label>
                <input
                  id="income-input"
                  type="number"
                  className="f1040-input"
                  min="0"
                  step="100"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  placeholder="e.g., 45,000"
                />
                <p className="f1040-help">
                  One combined income number for this teaching tool.
                </p>
              </div>
            </div>
          </section>

          {/* Children + dependents */}
          <section className="f1040-card">
            <h2>Children & dependents</h2>
            <div className="f1040-field-group">
              <label className="f1040-label" htmlFor="qc-input">
                Qualifying children (under 17)
              </label>
              <input
                id="qc-input"
                type="number"
                className="f1040-input"
                min="0"
                step="1"
                value={numQualifyingChildren}
                onChange={(e) => setNumQualifyingChildren(e.target.value)}
                placeholder="e.g., 2"
              />
            </div>

            <div className="f1040-field-group">
              <label className="f1040-label" htmlFor="od-input">
                Other dependents
              </label>
              <input
                id="od-input"
                type="number"
                className="f1040-input"
                min="0"
                step="1"
                value={numOtherDependents}
                onChange={(e) => setNumOtherDependents(e.target.value)}
                placeholder="e.g., 1"
              />
              <p className="f1040-help">
                Drives the Child Tax Credit and Other Dependent Credit amounts.
              </p>
            </div>
          </section>

          {/* Saver's Credit + retirement contributions + nonref table */}
          <section className="f1040-card">
            <h2>Saver&apos;s Credit & retirement contributions</h2>

            <div className="f1040-field-group">
              <label className="f1040-label">Saver&apos;s Credit</label>
              <label className="f1040-checkbox">
                <input
                  type="checkbox"
                  checked={saverUse}
                  onChange={(e) => setSaverUse(e.target.checked)}
                />
                <span>I made retirement contributions that might qualify</span>
              </label>
            </div>

            {saverUse && filingStatus === "single" && (
              <div className="f1040-field-group">
                <label className="f1040-label" htmlFor="saver-single">
                  Your qualified retirement contributions
                </label>
                <input
                  id="saver-single"
                  type="number"
                  className="f1040-input"
                  min="0"
                  step="100"
                  value={saverSingleContribution}
                  onChange={(e) => setSaverSingleContribution(e.target.value)}
                  placeholder="e.g., 2,000"
                />
                <label className="f1040-label" htmlFor="saver-single-type">
                  Type
                </label>
                <select
                  id="saver-single-type"
                  className="f1040-input"
                  value={saverSingleType}
                  onChange={(e) => setSaverSingleType(e.target.value)}
                >
                  <option value="traditional">
                    Traditional IRA (deductible here)
                  </option>
                  <option value="roth">
                    Roth IRA / 401(k) / other (not deductible here)
                  </option>
                </select>
                <p className="f1040-help">
                  For Saver&apos;s Credit, we count up to $2,000 of qualified
                  contributions. Only Traditional IRA amounts reduce AGI in this
                  tool.
                </p>
              </div>
            )}

            {saverUse && filingStatus === "mfj" && (
              <>
                <div className="f1040-field-group">
                  <label className="f1040-label" htmlFor="saver-sp1">
                    Spouse 1 contributions
                  </label>
                  <input
                    id="saver-sp1"
                    type="number"
                    className="f1040-input"
                    min="0"
                    step="100"
                    value={saverSpouse1Contribution}
                    onChange={(e) =>
                      setSaverSpouse1Contribution(e.target.value)
                    }
                    placeholder="e.g., 2,000"
                  />
                  <label className="f1040-label" htmlFor="saver-sp1-type">
                    Type
                  </label>
                  <select
                    id="saver-sp1-type"
                    className="f1040-input"
                    value={saverSpouse1Type}
                    onChange={(e) => setSaverSpouse1Type(e.target.value)}
                  >
                    <option value="traditional">
                      Traditional IRA (deductible here)
                    </option>
                    <option value="roth">
                      Roth IRA / 401(k) / other (not deductible here)
                    </option>
                  </select>
                </div>

                <div className="f1040-field-group">
                  <label className="f1040-label" htmlFor="saver-sp2">
                    Spouse 2 contributions
                  </label>
                  <input
                    id="saver-sp2"
                    type="number"
                    className="f1040-input"
                    min="0"
                    step="100"
                    value={saverSpouse2Contribution}
                    onChange={(e) =>
                      setSaverSpouse2Contribution(e.target.value)
                    }
                    placeholder="e.g., 2,000"
                  />
                  <label className="f1040-label" htmlFor="saver-sp2-type">
                    Type
                  </label>
                  <select
                    id="saver-sp2-type"
                    className="f1040-input"
                    value={saverSpouse2Type}
                    onChange={(e) => setSaverSpouse2Type(e.target.value)}
                  >
                    <option value="traditional">
                      Traditional IRA (deductible here)
                    </option>
                    <option value="roth">
                      Roth IRA / 401(k) / other (not deductible here)
                    </option>
                  </select>
                  <p className="f1040-help">
                    For Saver&apos;s Credit, each spouse is limited to $2,000 of
                    qualified contributions. Only Traditional IRA amounts reduce
                    AGI in this tool.
                  </p>
                </div>
              </>
            )}

            <div className="f1040-key-row" style={{ marginTop: 8 }}>
              <div className="f1040-key-main">
                <span className="f1040-key-label">
                  Saver&apos;s Credit calculated
                </span>
                <span className="f1040-key-value">
                  {formatCurrency(saverCalc)}
                </span>
              </div>
              <div className="f1040-key-explainer">
                Rate applied: {(saverRate * 100).toFixed(0)}% on eligible
                contributions.
              </div>
            </div>

            <div className="f1040-nonref-table-wrapper">
              <h3 className="f1040-subheading">Nonrefundable credits detail</h3>
              <NonrefTable
                results={nonrefResults}
                ctcCalc={ctcCalc}
                odcCalc={odcCalc}
                saverCalc={saverCalc}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

/**
 * ===== Helpers =====
 */

function parseNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function parseIntSafe(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 2025-ish standard deduction (you can swap numbers later if needed)
 * For now: Single / MFS = 15,750, MFJ = 31,500
 */
function getStandardDeduction(filingStatus) {
  if (filingStatus === "mfj") {
    return 31500;
  }
  return 15750;
}

/**
 * Simple 2025-style bracket function (placeholder).
 */
function calculateTax(ti, filingStatus) {
  ti = Math.max(0, ti);

  let brackets;

  if (filingStatus === "mfj") {
    brackets = [
      { max: 25620, rate: 0.1 },
      { max: 96480, rate: 0.12 },
      { max: 204180, rate: 0.22 },
      { max: 403400, rate: 0.24 },
      { max: 511120, rate: 0.32 },
      { max: 708750, rate: 0.35 },
      { max: Infinity, rate: 0.37 },
    ];
  } else {
    // SINGLE
    brackets = [
      { max: 12810, rate: 0.1 },
      { max: 48240, rate: 0.12 },
      { max: 102090, rate: 0.22 },
      { max: 201700, rate: 0.24 },
      { max: 255560, rate: 0.32 },
      { max: 603300, rate: 0.35 },
      { max: Infinity, rate: 0.37 },
    ];
  }

  let tax = 0;
  let prev = 0;

  for (let i = 0; i < brackets.length; i++) {
    const { max, rate } = brackets[i];
    if (ti > prev) {
      const amount = Math.min(ti, max) - prev;
      tax += amount * rate;
      prev = max;
    } else {
      break;
    }
  }

  return Math.round(tax);
}

function formatCurrency(value) {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function filingStatusLabel(filingStatus) {
  return filingStatus === "mfj" ? "Married filing jointly" : "Single";
}

/**
 * Saver's credit inputs (handle MFJ vs Single, plus Traditional vs Roth)
 * Returns:
 *  - totalEligible: contributions that count for Saver's Credit (capped at 2k per person)
 *  - totalDeductible: contributions that reduce AGI (traditional IRA only, capped here at 7k/person)
 */
function getSaverInputs({
  filingStatus,
  saverUse,
  saverSingleContribution,
  saverSingleType,
  saverSpouse1Contribution,
  saverSpouse1Type,
  saverSpouse2Contribution,
  saverSpouse2Type,
}) {
  if (!saverUse) {
    return { totalEligible: 0, totalDeductible: 0 };
  }

  const CAP_SAVER_PER_PERSON = 2000;
  const CAP_IRA_DED_PER_PERSON = 7000; // rough cap

  if (filingStatus === "mfj") {
    const sp1Raw = Math.max(0, parseNumber(saverSpouse1Contribution));
    const sp2Raw = Math.max(0, parseNumber(saverSpouse2Contribution));

    const sp1Eligible = Math.min(CAP_SAVER_PER_PERSON, sp1Raw);
    const sp2Eligible = Math.min(CAP_SAVER_PER_PERSON, sp2Raw);

    const sp1Deductible =
      saverSpouse1Type === "traditional"
        ? Math.min(CAP_IRA_DED_PER_PERSON, sp1Raw)
        : 0;

    const sp2Deductible =
      saverSpouse2Type === "traditional"
        ? Math.min(CAP_IRA_DED_PER_PERSON, sp2Raw)
        : 0;

    return {
      totalEligible: sp1Eligible + sp2Eligible,
      totalDeductible: sp1Deductible + sp2Deductible,
    };
  }

  // Single
  const singleRaw = Math.max(0, parseNumber(saverSingleContribution));
  const singleEligible = Math.min(CAP_SAVER_PER_PERSON, singleRaw);
  const singleDeductible =
    saverSingleType === "traditional"
      ? Math.min(CAP_IRA_DED_PER_PERSON, singleRaw)
      : 0;

  return {
    totalEligible: singleEligible,
    totalDeductible: singleDeductible,
  };
}

/**
 * Saver's credit rate based on AGI + filing status (very simplified)
 */
function getSaversCreditRate(agi, filingStatus) {
  if (filingStatus === "mfj") {
    if (agi <= 47500) return 0.5;
    if (agi <= 51000) return 0.2;
    if (agi <= 79000) return 0.1;
    return 0;
  } else {
    // single
    if (agi <= 23750) return 0.5;
    if (agi <= 25500) return 0.2;
    if (agi <= 39500) return 0.1;
    return 0;
  }
}

/**
 * Apply nonref credits in order and track used vs unused.
 * credits is array of { key, label, amount }
 */
function applyNonrefCreditsInOrder(taxBeforeCredits, credits) {
  let remainingTax = Math.max(0, taxBeforeCredits);

  const rows = [];
  let totalUsed = 0;
  let totalUnused = 0;

  credits.forEach((credit) => {
    const calc = Math.max(0, credit.amount || 0);
    const used = Math.min(calc, remainingTax);
    const unused = calc - used;

    remainingTax -= used;
    totalUsed += used;
    totalUnused += unused;

    rows.push({
      key: credit.key,
      label: credit.label,
      calculated: calc,
      used,
      unused,
    });
  });

  return {
    rows,
    remainingTax,
    totalUsed,
    totalUnused,
  };
}

/**
 * ===== Presentational components =====
 */

const SpineStep = ({ step, label, value, explanation }) => (
  <div className="f1040-spine-step">
    <div className="f1040-spine-marker">{step}</div>
    <div className="f1040-spine-content">
      <div className="f1040-spine-header">
        <span className="f1040-spine-label">{label}</span>
        <span className="f1040-spine-value">{value}</span>
      </div>
      {explanation && (
        <div className="f1040-spine-explainer">{explanation}</div>
      )}
    </div>
  </div>
);

const NonrefTable = ({ results, ctcCalc, odcCalc, saverCalc }) => {
  if (!results || !results.rows) return null;

  const getRow = (key, fallbackCalc) => {
    const row = results.rows.find((r) => r.key === key);
    if (!row) {
      return {
        calculated: fallbackCalc || 0,
        used: 0,
        unused: fallbackCalc || 0,
      };
    }
    return row;
  };

  const ctcRow = getRow("ctc", ctcCalc);
  const odcRow = getRow("odc", odcCalc);
  const saverRow = getRow("saver", saverCalc);

  return (
    <table className="f1040-nonref-table">
      <thead>
        <tr>
          <th>Credit</th>
          <th className="f1040-nonref-right">Calculated</th>
          <th className="f1040-nonref-right">Used</th>
          <th className="f1040-nonref-right">Unused</th>
        </tr>
      </thead>
      <tbody>
        <NonrefRow label="Child Tax Credit (nonrefundable)" row={ctcRow} />
        <NonrefRow label="Other Dependent Credit" row={odcRow} />
        <NonrefRow label="Saver's Credit" row={saverRow} />
      </tbody>
    </table>
  );
};

const NonrefRow = ({ label, row }) => (
  <tr>
    <td>{label}</td>
    <td className="f1040-nonref-right">
      {formatCurrency(row.calculated)}
    </td>
    <td className="f1040-nonref-right">{formatCurrency(row.used)}</td>
    <td className="f1040-nonref-right">
      {formatCurrency(row.unused)}
    </td>
  </tr>
);

export default Form1040Flow;
